define('@getgo/telemetry-client/telemetry',[],function () { 'use strict';

  var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && typeof Symbol === "function" && obj.constructor === Symbol ? "symbol" : typeof obj;
  };

  var reduce = Array.prototype.reduce;

  /**
   * @private
   *
   * @param {...object} var_args
   *
   * @returns {object}
   */
  function merge() /* var_args */{
      return reduce.call(arguments, function (result, source) {
          if (source && (typeof source === 'undefined' ? 'undefined' : _typeof(source)) == 'object') {
              Object.keys(source).forEach(function (property) {
                  result[property] = source[property];
              });
          }

          return result;
      });
  }

  /**
   * @typedef {object} Tag
   *
   * @property {string} name
   * @property {string|number|boolean} value
   */

  /**
   * @typedef {object} TagData
   *
   * @property {string|number|boolean} ...*
   */

  /**
   *
   * @class TagSet
   */
  function TagSet() {

      var entries = {};

      /**
       * @memberOf TagSet#
       *
       * @param {string|Tag} name
       * @param {string|number|boolean} [value] the value, if name is a string
       */
      function setTag(name, value) {

          var tag;

          if ((typeof name === 'undefined' ? 'undefined' : _typeof(name)) == 'object') {
              tag = { name: tag.name, value: tag.value };
          } else {
              tag = { name: name, value: value };
          }

          if (typeof tag.name == 'string') {
              if (tag.value === undefined) {
                  if (entries.hasOwnProperty(tag.name)) {
                      delete entries[tag.name];
                  }
              } else {
                  entries[tag.name] = tag.value;
              }
          }
      }

      this.setTag = setTag;

      /**
       * @memberOf TagSet#
       *
       * @param {TagData} tags
       */
      function setTags(tags) {
          if ((typeof tags === 'undefined' ? 'undefined' : _typeof(tags)) == 'object') {
              entries = tags || {};
          }
      }

      this.setTags = setTags;

      /**
       * @memberOf TagSet#
       *
       * @returns {TagData}
       */
      function toEventData() {
          return merge({}, entries);
      }

      this.toEventData = toEventData;
  }

  /**
   * @typedef {object} CounterEvent
   *
   * @property {number} value
   */

  /**
   * @class Counter
   *
   */
  function Counter() {

    var tags = new TagSet();

    var value = 0.0;

    /**
     * @memberOf Counter#
     *
     * @param {number} [val=1]
     */
    function decrement(val) {
      value = value - (parseFloat(val, 10) || 1.0);
    }

    this.decrement = decrement;

    /**
     * @memberOf Counter#
     *
     * @returns {number}
     */
    function getValue() {
      return value;
    }

    this.getValue = getValue;

    /**
     * @memberOf Counter#
     *
     * @param {number} [val=1]
     */
    function increment(val) {
      value = value + (parseFloat(val, 10) || 1.0);
    }

    this.increment = increment;

    /**
     * @memberOf Counter#
     */
    function reset() {
      value = 0;
    }

    this.reset = reset;

    /**
     * @method Counter#setTags
     *
     * @see TagSet#setTags
     */

    this.setTags = tags.setTags;

    /**
     * @method Counter#setTag
     *
     * @see TagSet#setTag
     */

    this.setTag = tags.setTag;

    /**
     * @memberOf Counter#
     *
     * @returns {CounterEvent}
     */
    function toEventData() {
      var tagData = tags.toEventData();

      return Object.assign(tagData, {
        value: value
      });
    }

    this.toEventData = toEventData;
  }

  function isFunction(func) {
      return typeof func == 'function';
  }

  function isObject(object) {
      return object && (typeof object === 'undefined' ? 'undefined' : _typeof(object)) == 'object';
  }

  function hasMethod(object, method) {
      return isObject(object) && isFunction(object[method]);
  }

  function wrap(target, method, func) {
      var base;

      if (hasMethod(target, method)) {
          base = target[method];

          target[method] = function () {
              return func.call(target, base.apply(target, arguments));
          };
      } else {
          target[method] = func;
      }

      return target;
  }

  function decorate(target, method, func) {

      if (!isObject(target)) {
          throw Error('Invalid argument: target');
      }

      if (isObject(method)) {
          Object.keys(method).forEach(function (methodname) {
              var methodvalue = method[methodname];

              if (isFunction(methodvalue)) {
                  wrap(target, methodname, methodvalue);
              }
          });

          return target;
      } else {

          if (!isFunction(func)) {
              throw Error('Invalid argument: func');
          }

          return wrap(target, method, func);
      }
  }

  /**
   * @class Histogram
   *
   */
  function Histogram(reservoir) {

    var tags = new TagSet();

    /**
     * @memberOf Histogram#
     *
     * @returns {Snapshot}
     */
    function getSnapshot() {

      var tagData = tags.toEventData();

      return decorate(reservoir.getSnapshot(), 'toEventData', function (evt) {
        return Object.assign(tagData, evt);
      });
    }

    this.getSnapshot = getSnapshot;

    /**
     * @method Histogram#setTags
     *
     * @see TagSet#setTags
     */

    this.setTags = tags.setTags;

    /**
     * @method Histogram#setTag
     *
     * @see TagSet#setTag
     */

    this.setTag = tags.setTag;

    /**
     * @memberOf Histogram#
     *
     * @param {number} [value=1]
     */
    function update(value) {
      return reservoir.update(parseFloat(value, 10) || 1.0);
    }

    this.update = update;
  }

  /**
   * @class Registry
   *
   */
  function Registry() {

      var contents = {};

      /**
       * @memberOf Registry#
       *
       * @param {string} type
       * @param {string} id
       *
       * @returns {*}
       */
      function get(type, id) {
          if (id && contents[type]) {
              return contents[type][id];
          }

          return contents[type];
      }

      this.get = get;

      /**
       * @memberOf Registry#
       *
       * @param {string} type
       * @param {string} id
       * @param {*} item
       */
      function set(type, id, item) {
          if (!contents[type]) {
              contents[type] = {};
          }

          if (item) {
              contents[type][id] = item;
          } else if (contents[type][id]) {
              delete contents[type][id];
          }
      }

      this.set = set;
  }

  function average(data) {
      if (data.length < 1) {
          return 0;
      }

      var sum = data.reduce(function (s, i) {
          return s + i;
      }, 0);

      return sum / data.length;
  }

  function compareNumbers(a, b) {
      return a - b;
  }

  /**
   * @class Snapshot
   *
   * @param {Array<number>} val
   * @param {boolean} [useSlice=false]
   */
  function Snapshot(val, useSlice) {

      var values = useSlice ? val.slice(0) : val.splice(0);

      values.sort(compareNumbers);

      /**
       * Returns the highest value in the snapshot
       *
       * @memberOf Snapshot#
       *
       * @return {number} the highest value
       */
      function getMax() {
          if (values.length < 1) {
              return 0;
          }
          return values[values.length - 1];
      }

      this.getMax = getMax;

      /**
       * Returns the median value in the distribution.
       *
       * @memberOf Snapshot#
       *
       * @return {number} the median value
       */
      function getMedian() {
          return getQuantile(0.5);
      }

      this.getMedian = getMedian;

      /**
       * Returns the arithmetic mean of the values in the snapshot
       *
       * @memberOf Snapshot#
       *
       * @return {number} the arithmetic mean
       */

      function getMean() {
          return average(values);
      }

      this.getMean = getMean;

      /**
       * Returns the lowest value in the snapshot
       *
       * @memberOf Snapshot#
       *
       * @return {number} the lowest value
       */

      function getMin() {
          if (values.length < 1) {
              return 0;
          }
          return values[0];
      }

      this.getMin = getMin;

      /**
       * Returns the value at the given quantile
       *
       * @memberOf Snapshot#
       *
       * @param {number} quantile  [0..1]
       *
       * @return {number} the value in the distribution at {quantile}
       */
      function getQuantile(quantile) {

          var qq = parseFloat(quantile, 10);

          if (qq < 0.0 || qq > 1.0 || isNaN(qq)) {
              throw Error(qq + " is not in [0..1]");
          }

          if (values.length < 1) {
              return 0.0;
          }

          var pos = qq * (values.length + 1);
          var index = parseInt(pos);

          if (index < 1) {
              return values[0];
          }

          if (index >= values.length) {
              return values[values.length - 1];
          }

          var lower = values[index - 1];
          var upper = values[index];

          return lower + (pos - Math.floor(pos)) * (upper - lower);
      }

      this.getQuantile = getQuantile;

      function getSize() {
          return values.length;
      }

      this.getSize = getSize;

      /**
       * Returns the standard deviation of the values in the snapshot
       *
       * @memberOf Snapshot#
       *
       * @return {number} the standard deviation value
       */
      function getStandardDeviation() {
          return Math.sqrt(getVariance());
      }

      this.getStandardDeviation = getStandardDeviation;

      /**
       * Returns the variance of the values in the snapshot
       *
       * @memberOf Snapshot#
       *
       * @returns {number}
       */
      function getVariance() {

          if (values.length <= 1) {
              return 0;
          }

          var mean = getMean();

          var squarediffs = values.map(function (value) {
              var diff = value - mean;

              return diff * diff;
          });

          return average(squarediffs);
      }

      this.getVariance = getVariance;

      /**
       * @memberOf Snapshot#
       *
       * @returns {SnapshotEvent}
       */
      function toEventData() {
          /**
           * @typedef {object} SnapshotEvent
           *
           * @property {number} max
           * @property {number} mean
           * @property {number} median
           * @property {number} min
           * @property {number} sigma
           * @property {number} size
           * @property {number} 75percentile
           * @property {number} 90percentile
           * @property {number} 95percentile
           * @property {number} 98percentile
           * @property {number} 99percentile
           */

          var val = {
              max: getMax(),
              mean: getMean(),
              median: getMedian(),
              min: getMin(),
              sigma: getStandardDeviation(),
              size: getSize()
          };

          [75, 90, 95, 98, 99].forEach(function (quantile) {
              val[quantile + 'percentile'] = getQuantile(quantile / 100);
          });

          return val;
      }

      this.toEventData = toEventData;
  }

  /**
   * A collection of values that is emptied automatically when a Snapshot is taken
   *
   * @class ResettingReservoir
   *
   */
  function ResettingReservoir() {
    var values = [];

    /**
     * @memberOf ResettingReservoir#
     *
     * @returns {Snapshot}
     */
    function getSnapshot() {
      return new Snapshot(values);
    }

    this.getSnapshot = getSnapshot;

    /**
     * @memberOf ResettingReservoir#
     *
     */
    function reset() {
      values.length = 0;
    }

    this.reset = reset;

    /**
     * @memberOf ResettingReservoir#
     *
     * @param {number} value
     */
    function update(value) {
      values.push(value);
    }

    this.update = update;
  }

  function sum(a, b) {
      return a + b;
  }

  /**
   * @class Duration
   *
   */
  function Duration() {

      var starts = [];

      var stops = [];

      /**
       * @memberOf Duration#
       *
       * @returns {number}
       */
      function getValue() {
          return stops.map(function (stop, idx) {
              return stop - starts[idx];
          }).reduce(sum, 0);
      }

      this.getValue = getValue;

      /**
       * @memberOf Duration#
       */
      function reset() {
          starts.length = 0;
          stops.length = 0;
      }

      this.reset = reset;

      /**
       * @memberOf Duration#
       */
      function start() {
          if (starts.length == stops.length) {
              starts.push(Date.now());
          }
      }

      this.start = start;

      /**
       * @memberOf Duration#
       */
      function stop() {
          if (starts.length == stops.length + 1) {
              stops.push(Date.now());
          }
      }

      this.stop = stop;
  }

  /**
   * @class TimeContext
   *
   * @param {function} sink
   *
   * @param {Duration} duration
   *
   */
  function TimeContext(sink, duration) {

    /**
     * @memberOf TimeContext#
     */
    function pause() {
      duration.stop();
    }

    this.pause = pause;

    /**
     * @memberOf TimeContext#
     */
    function resume() {
      duration.start();
    }

    this.resume = resume;

    /**
     * @memberOf TimeContext#
     */
    function stop() {
      duration.stop();
      sink(duration.getValue());
      duration.reset();
    }

    this.stop = stop;
  }

  function isThenable(obj) {
      return obj instanceof Promise || obj && (typeof obj === 'undefined' ? 'undefined' : _typeof(obj)) == 'object' && typeof obj.then == 'function';
  }

  // TODO: At a meter for call rate

  /**
   * A histogram of the duration of a type of event
   *
   * @class Timer
   *
   * @param {Reservoir} reservoir
   */
  function Timer(reservoir) {

      var tags = new TagSet();

      /**
       * @memberOf Timer#
       *
       * @returns {Snapshot}
       */
      function getSnapshot() {

          var tagData = tags.toEventData();

          return decorate(reservoir.getSnapshot(), 'toEventData', function (evt) {
              return Object.assign(tagData, evt);
          });
      }

      this.getSnapshot = getSnapshot;

      /**
       * @memberOf Timer#
       *
       * @param {function|Promise} runnable
       *
       * @returns {*}
       */
      function record(runnable) {
          var func = typeof runnable == 'function' ? runnable : function () {
              return runnable;
          };

          var duration = new Duration();

          var stop = function stop() {
              duration.stop();

              reservoir.update(duration.getValue());
          };

          var result;

          try {
              duration.start();

              result = func();

              if (isThenable(result)) {
                  result.then(stop, stop);
              } else {
                  stop();
              }
          } catch (e) {
              stop();

              throw e;
          }

          return result;
      }

      this.record = record;

      /**
       * @method Timer#setTags
       *
       * @see TagSet#setTags
       */

      this.setTags = tags.setTags;

      /**
       * @method Timer#setTag
       *
       * @see TagSet#setTag
       */

      this.setTag = tags.setTag;

      /**
       * @private
       * @param {number} value
       */
      function sink(value) {
          reservoir.update(value);
      }

      /**
       * @memberOf Timer#
       *
       * @returns {TimeContext}
       */
      function time() {
          var context = new TimeContext(sink, new Duration());

          context.resume();

          return context;
      }

      this.time = time;
  }

  var slice = Array.prototype.slice;

  var instanceOptions = ['ev_source', 'id_run', 'id_dev'];

  var schemas = ['ep_telemetry', 'soa_telemetry', 'tnps_telemetry'];

  /**
   * @typedef {object} SchemaIdMap
   *
   * @property {string} [Counter]
   * @property {string} [Histogram]
   * @property {string} [Timer]
   */

  /**
   * @typedef {object} TelemetryOptions
   *
   * @property {string} [ev_schema_name]
   * @property {string} [ev_schema_ver]
   * @property {string} ev_source
   * @property {string} id_dev
   * @property {string} id_run
   * @property {SchemaIdMap} [schemas]
   *
   */

  /**
   * @private
   *
   */
  function formatTimezoneOffset() {
      var utcOffset = -(new Date().getTimezoneOffset() / 60);

      var hours = utcOffset < 0 ? Math.ceil(utcOffset) : Math.floor(utcOffset);

      var minutes = (Math.abs(utcOffset) % 1).toFixed(2) * 60;

      return hours + (String(minutes).length > 1 ? ':' : ':0') + minutes;
  }

  function validateOptions(options) {
      if (!options || (typeof options === 'undefined' ? 'undefined' : _typeof(options)) != 'object') {
          throw Error('Invalid argument: options');
      }

      instanceOptions.forEach(function (option) {
          var value = options[option];

          if (!value || typeof value != 'string') {
              throw Error('Invalid option: ' + option);
          }
      });

      if (options.ev_schema_name || options.ev_schema_ver) {
          if (schemas.indexOf(options.ev_schema_name) < 0) {
              throw Error('Invalid option: ev_schema_name');
          }

          if (isNaN(parseInt(options.ev_schema_ver, 10))) {
              throw Error('Invalid option: ev_schema_ver');
          }
      }
  }

  /**
   * @class Telemetry
   *
   * @param {TransportFunction} transport
   *
   * @param {TelemetryOptions} options
   */
  function Telemetry(transport, options) {

      validateOptions(options);

      var globals = {};

      var lastIndex = 1;

      var registry = new Registry();

      setInterval(report, 60 * 1000);

      function getIndex() {
          var idx = lastIndex;

          lastIndex = lastIndex + 1 == idx ? 1 : lastIndex + 1;

          return idx;
      }

      function getInstance(constructor, name, reservoir) {
          var instance = registry.get(constructor.name, name);

          if (!instance) {
              instance = constructor.length ? new constructor(reservoir || new ResettingReservoir()) : new constructor();

              registry.set(constructor.name, name, instance);
          }

          return instance;
      }

      function makeEvent(name) {
          if (typeof name != 'string') {
              throw Error('Invalid argument: name');
          }

          var defaults = {
              os_tz: formatTimezoneOffset()
          };

          var required = {
              ev_index: getIndex(),
              ev_name: name,
              ev_source: options.ev_source,
              ev_time: new Date().toISOString(),
              id_dev: options.id_dev,
              id_run: options.id_run
          };

          var data = slice.call(arguments, 1);

          data.unshift(defaults, options.attributes, globals);

          data.push(required);

          return merge.apply(null, data);
      }

      function report() {
          var events = [];

          var schemas = options.schemas || {};

          [Histogram, Timer].forEach(function (type) {
              var producers = registry.get(type.name);

              if (producers) {
                  Object.keys(producers).forEach(function (name) {
                      var snapshot = producers[name].getSnapshot();

                      if (snapshot.getSize()) {
                          events.push(makeEvent([name, type.name].join('.'), snapshot.toEventData(), schemas[type.name]));
                      }
                  });
              }
          });

          [Counter].forEach(function (type) {
              var producers = registry.get(type.name);

              if (producers) {
                  Object.keys(producers).forEach(function (name) {
                      var counterEvent = producers[name].toEventData();

                      events.push(makeEvent([name, type.name].join('.'), counterEvent, schemas[type.name]));
                  });
              }
          });

          transport.apply(null, events);
      }

      /**
       * @memberOf Telemetry#
       *
       * @param {string} name
       *
       * @returns {Counter}
       */
      function counter(name) {
          return getInstance(Counter, name);
      }

      this.counter = counter;

      /**
       * @memberOf Telemetry#
       *
       * @param {string} name
       * @param {...object} [attributes]
       *
       * @returns {Promise}
       */
      function event() /* name, attributes */{
          return transport(makeEvent.apply(null, arguments));
      }

      this.event = event;

      /**
       * @memberOf Telemetry#
       *
       * @param {string} name
       * @param {Reservoir} [reservoir]
       *
       * @returns {Histogram}
       */
      function histogram(name, reservoir) {
          return getInstance(Histogram, name, reservoir);
      }

      this.histogram = histogram;

      /**
       * @memberOf Telemetry#
       *
       * @param {object} attributes
       */
      function setGlobalAttributes(attributes) {
          globals = attributes;
      }

      this.setGlobalAttributes = setGlobalAttributes;

      /**
       * @memberOf Telemetry#
       *
       * @param {string} name
       * @param {*} value
       */
      function setGlobalAttribute(name, value) {
          if (typeof name == 'string') {
              if (value === undefined) {
                  if (globals.hasOwnProperty(name)) {
                      delete globals[name];
                  }
              } else {
                  globals[name] = value;
              }
          }
      }

      this.setGlobalAttribute = setGlobalAttribute;

      /**
       * @memberOf Telemetry#
       *
       * @param {string} name
       * @param {Reservoir} [reservoir]
       *
       * @returns {Timer}
       */
      function timer(name, reservoir) {
          return getInstance(Timer, name, reservoir);
      }

      this.timer = timer;
  }

  /**
   * @class Factory
   */
  function Factory() {

      /**
       * @memberOf Factory
       *
       * @param {TransportFunction} transport
       * @param {TelemetryOptions} options
       *
       * @returns {Telemetry}
       */
      function createClient(transport, options) {

          if (typeof transport != 'function') {
              throw Error('transport has to be a function');
          }

          return new Telemetry(transport, options);
      }

      this.createClient = createClient;
  }

  var factory = new Factory();

  return factory;

});
